import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { adminSignIn, checkAuthState } from '../Functions/AdminAuth';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useDarkMode } from '../Functions/DarkMode';
import { FaLock, FaEnvelope, FaSpinner } from 'react-icons/fa';
import Header from '../components/header';
import Footer from '../components/Footer';

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.darkMode ? '#121212' : '#f8f9fa'};
  padding: 20px;
`;

const LoginCard = styled(motion.div)`
  background-color: ${props => props.darkMode ? '#1e1e1e' : '#ffffff'};
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, ${props => props.darkMode ? '0.3' : '0.1'});
  width: 100%;
  max-width: 450px;
  overflow: hidden;
  padding: 0;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #3498db, #2c3e50);
  padding: 25px;
  text-align: center;
  color: white;
`;

const CardBody = styled.div`
  padding: 30px;
  color: ${props => props.darkMode ? '#f5f5f5' : '#333'};
`;

const LogoWrapper = styled.div`
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.darkMode ? '#aaa' : '#666'};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 14px 14px 14px 45px;
  border-radius: 8px;
  border: 1px solid ${props => props.darkMode ? '#444' : '#ddd'};
  background-color: ${props => props.darkMode ? '#333' : '#fff'};
  color: ${props => props.darkMode ? '#f5f5f5' : '#333'};
  font-size: 16px;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
  
  &::placeholder {
    color: ${props => props.darkMode ? '#888' : '#aaa'};
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 14px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  &:disabled {
    background-color: ${props => props.darkMode ? '#444' : '#aaa'};
    cursor: not-allowed;
  }
`;

const AdminAuth = () => {
  const { darkMode } = useDarkMode();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const unsubscribe = checkAuthState((user) => {
      if (user) {
        navigate('/admin');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { email, password } = credentials;
      
      if (!email || !password) {
        toast.error('Please enter both email and password');
        setLoading(false);
        return;
      }
      
      const result = await adminSignIn(email, password);
      
      if (result.success) {
        toast.success('Login successful!');
        navigate('/admin');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header currentPage="admin" />
      <AuthContainer darkMode={darkMode}>
        <LoginCard 
          darkMode={darkMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <CardHeader>
            <LogoWrapper>
              <motion.img 
                src="/assets/Images/logo.png" 
                alt="LSQ Stays Admin"
                height="50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              />
            </LogoWrapper>
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={{ fontSize: '1.8rem', fontWeight: '600' }}
            >
              Admin Login
            </motion.h1>
          </CardHeader>
          
          <CardBody darkMode={darkMode}>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <InputIcon darkMode={darkMode}>
                  <FaEnvelope />
                </InputIcon>
                <FormInput
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                  darkMode={darkMode}
                />
              </FormGroup>
              
              <FormGroup>
                <InputIcon darkMode={darkMode}>
                  <FaLock />
                </InputIcon>
                <FormInput
                  type="password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Password"
                  required
                  darkMode={darkMode}
                />
              </FormGroup>
              
              <SubmitButton
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                darkMode={darkMode}
              >
                {loading ? (
                  <>
                    <FaSpinner className="fa-spin" /> Logging in...
                  </>
                ) : (
                  'Login to Dashboard'
                )}
              </SubmitButton>
            </form>
          </CardBody>
        </LoginCard>
      </AuthContainer>
      <Footer />
    </>
  );
};

export default AdminAuth;
